'use client';

import { useState, useEffect } from 'react';

const API_BASE = 'https://app.trip-planner.com.au/api';

interface FishSpecies {
  name: string;
  scientific_name: string;
  habitat: string;
  description: string;
  best_months: string;
  best_season: string;
  min_size_cm: number;
  bag_limit: number;
  difficulty: string;
  methods: string;
  image_url: string;
  in_season: boolean;
  confidence: string;
}

const HABITAT_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  freshwater: { label: 'Freshwater', color: 'text-[#1558b0]', bg: 'bg-[#e3f2fd] border-[#e3f2fd]' },
  saltwater: { label: 'Saltwater', color: 'text-cyan-700', bg: 'bg-cyan-50 border-cyan-200' },
  estuarine: { label: 'Estuarine', color: 'text-teal-700', bg: 'bg-teal-50 border-teal-200' },
  both: { label: 'Fresh & Salt', color: 'text-purple-700', bg: 'bg-purple-50 border-purple-200' },
};

const DIFFICULTY_COLORS: Record<string, string> = {
  Easy: 'text-green-600',
  Medium: 'text-amber-600',
  Hard: 'text-red-600',
};

const STORE = 'https://campingaustralia.com.au/collections';
const METHOD_LINKS: Record<string, string> = {
  'Lure': `${STORE}/lures`,
  'Bait': `${STORE}/fishing`,
  'Fly': `${STORE}/fishing`,
  'Soft Plastics': `${STORE}/lures`,
  'Metal Slug': `${STORE}/lures`,
  'Live Bait': `${STORE}/fishing`,
  'Trolling': `${STORE}/fishing-combos`,
  'Jigging': `${STORE}/lures`,
  'Popping': `${STORE}/lures`,
  'Sight Casting': `${STORE}/fishing-rods`,
  'Float': `${STORE}/fishing`,
  'Green Weed': `${STORE}/fishing`,
  'Squid Jig': `${STORE}/lures`,
  'Net': `${STORE}/nets-traps`,
  'Hand Line': `${STORE}/fishing-line`,
  'Crab Pot': `${STORE}/nets-traps`,
  'Scoop Net': `${STORE}/nets-traps`,
};

function FishDetailModal({ fish, onClose }: { fish: FishSpecies; onClose: () => void }) {
  const habitat = HABITAT_LABELS[fish.habitat] || HABITAT_LABELS.freshwater;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative bg-white rounded-2xl max-w-md w-full max-h-[85vh] overflow-y-auto shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/80 hover:bg-white flex items-center justify-center text-slate-500 hover:text-slate-700 shadow-sm"
        >
          ✕
        </button>

        {/* Hero image */}
        {fish.image_url ? (
          <div className="w-full h-56 bg-slate-100">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={fish.image_url.replace('/thumbnail', '/original')}
              alt={fish.name}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="w-full h-32 bg-gradient-to-br from-[#e3f2fd] to-cyan-50 flex items-center justify-center text-5xl">
            🐟
          </div>
        )}

        <div className="p-5">
          {/* Name + badges */}
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="text-xl font-bold text-slate-800">{fish.name}</h3>
              <p className="text-sm text-slate-400 italic">{fish.scientific_name}</p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className={`text-[11px] font-medium px-2 py-0.5 rounded border ${habitat.bg} ${habitat.color}`}>
                {habitat.label}
              </span>
              {fish.in_season && (
                <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-green-50 border border-green-200 text-green-700">
                  In Season
                </span>
              )}
            </div>
          </div>

          {/* Full description */}
          <p className="text-sm text-slate-600 mt-3 leading-relaxed">{fish.description}</p>

          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Best Season</div>
              <div className="text-sm text-slate-700 mt-0.5">{fish.best_season}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Difficulty</div>
              <div className={`text-sm font-medium mt-0.5 ${DIFFICULTY_COLORS[fish.difficulty] || 'text-slate-700'}`}>
                {fish.difficulty}
              </div>
            </div>
            {fish.min_size_cm > 0 && (
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Min Legal Size</div>
                <div className="text-sm text-slate-700 mt-0.5">{fish.min_size_cm} cm</div>
              </div>
            )}
            {fish.bag_limit > 0 && fish.bag_limit < 200 && (
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Bag Limit</div>
                <div className="text-sm text-slate-700 mt-0.5">{fish.bag_limit} per person</div>
              </div>
            )}
          </div>

          {/* Methods — linked to store */}
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1.5">Fishing Methods — Shop Gear</div>
            <div className="flex flex-wrap gap-1.5">
              {fish.methods.split(', ').map(method => {
                const link = METHOD_LINKS[method];
                if (link) {
                  return (
                    <a
                      key={method}
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs px-2 py-1 rounded-full bg-[#e3f2fd] text-[#1558b0] border border-[#e3f2fd] hover:bg-[#e3f2fd] hover:border-[#e3f2fd] transition-colors inline-flex items-center gap-1"
                    >
                      {method}
                      <svg className="w-2.5 h-2.5 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                    </a>
                  );
                }
                return (
                  <span key={method} className="text-xs px-2 py-1 rounded-full bg-slate-50 text-slate-600 border border-slate-200">
                    {method}
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 text-[10px] text-slate-400">
          Always check local regulations · Image: Atlas of Living Australia (CC)
        </div>
      </div>
    </div>
  );
}

export default function FishingSpecies({ entityId }: { entityId: number }) {
  const [species, setSpecies] = useState<FishSpecies[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [selectedFish, setSelectedFish] = useState<FishSpecies | null>(null);

  useEffect(() => {
    const fetchSpecies = async () => {
      try {
        const res = await fetch(`${API_BASE}/entities/${entityId}/fish-species`);
        if (res.ok) {
          const data = await res.json();
          setSpecies(data.species || []);
        }
      } catch (err) {
        console.error('Failed to load fish species:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSpecies();
  }, [entityId]);

  if (loading || species.length === 0) return null;

  const inSeason = species.filter(s => s.in_season);
  const displaySpecies = expanded ? species : species.slice(0, 6);

  return (
    <>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-slate-800">Fishing Guide</h3>
            <span className="text-xs text-slate-400">
              {species.length} species{inSeason.length > 0 && ` · ${inSeason.length} in season`}
            </span>
          </div>
          {inSeason.length > 0 && (
            <div className="flex items-center gap-1.5 mt-1.5">
              <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
              <span className="text-xs text-green-700">
                In season: {inSeason.slice(0, 4).map(s => s.name).join(', ')}
                {inSeason.length > 4 && ` +${inSeason.length - 4} more`}
              </span>
            </div>
          )}
        </div>

        {/* Species Cards */}
        <div className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {displaySpecies.map((sp) => {
              const habitat = HABITAT_LABELS[sp.habitat] || HABITAT_LABELS.freshwater;
              return (
                <button
                  key={sp.name}
                  onClick={() => setSelectedFish(sp)}
                  className={`rounded-xl border overflow-hidden transition-all hover:shadow-md text-left cursor-pointer ${
                    sp.in_season ? 'border-green-200 bg-green-50/20' : 'border-slate-200 bg-slate-50/30'
                  }`}
                >
                  <div className="flex items-start gap-3 p-3">
                    {/* Fish thumbnail */}
                    {sp.image_url ? (
                      <div className="w-14 h-14 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={sp.image_url}
                          alt={sp.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                    ) : (
                      <div className="w-14 h-14 rounded-lg bg-slate-100 flex-shrink-0 flex items-center justify-center text-xl">
                        🐟
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-1">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1">
                            <span className="font-semibold text-sm text-slate-800 leading-tight truncate">{sp.name}</span>
                            {sp.in_season && (
                              <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-green-500" />
                            )}
                          </div>
                          <div className="text-[10px] text-slate-400 italic truncate">{sp.scientific_name}</div>
                        </div>
                        <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded border flex-shrink-0 ${habitat.bg} ${habitat.color}`}>
                          {habitat.label}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-x-2 mt-1 text-[10px] text-slate-500">
                        <span>{sp.best_season}</span>
                      </div>
                      <div className="flex items-center gap-1 mt-0.5 text-[10px] text-slate-400">
                        <span className={DIFFICULTY_COLORS[sp.difficulty] || ''}>{sp.difficulty}</span>
                        <span>·</span>
                        <span className="truncate">{sp.methods}</span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {species.length > 6 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-3 w-full text-center text-sm text-[#1558b0] hover:text-[#1558b0] font-medium py-1.5 rounded-lg hover:bg-[#e3f2fd] transition-colors"
            >
              {expanded ? 'Show less' : `Show all ${species.length} species`}
            </button>
          )}
        </div>

        <div className="px-5 py-2 border-t border-slate-100 flex items-center justify-between">
          <span className="text-[10px] text-slate-400">Check local regulations before fishing</span>
          <span className="text-[10px] text-slate-400">Images: Atlas of Living Australia</span>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedFish && (
        <FishDetailModal fish={selectedFish} onClose={() => setSelectedFish(null)} />
      )}
    </>
  );
}
